import React, { useMemo } from 'react';
import jsonData from '../../signals/jsonData';
import appState from '../../signals/appState';
import translation from '../../signals/translations';

const RouteInfoPanel = () => {
	const { activeRoute, activeFloor } = appState.value;
	const { recommendedRoutes, exhibitorInfo, floorInfo } = jsonData.value;

	const routeData = useMemo(() => {
		if (!activeRoute || !recommendedRoutes || !recommendedRoutes.length) return null;

		const rows = recommendedRoutes.filter((r) => r['路線ID'] === activeRoute);
		if (!rows.length) return null;

		return {
			id: activeRoute,
			name: rows[0]['路線名稱'] || '',
			stops: rows.sort((a, b) => Number(a['站序']) - Number(b['站序'])),
		};
	}, [activeRoute, recommendedRoutes]);

	const resolvedStops = useMemo(() => {
		if (!routeData) return [];

		let boothIndex = 0;
		return routeData.stops.map((stop) => {
			const isBooth = stop['點位類型'] === '攤位';
			const isPassage = stop['點位類型'] === '樓層通道';
			let floorIndex = -1;
			let floorLabel = '';
			let name = stop['攤位號碼'] || '';

			let intro = stop['廠商介紹'] || '';
			if (isBooth) {
				boothIndex++;
				const boothNumber = stop['攤位號碼'];
				const exhibitor = exhibitorInfo.find((e) => e['攤位號碼'] === boothNumber);
				if (exhibitor) {
					name = exhibitor['廠商簡稱'] || exhibitor['廠商名稱'] || boothNumber;
					if (!intro) intro = exhibitor['展商基本介紹'] || '';
				}
				floorInfo.forEach((floor, index) => {
					if (floor.booths) {
						const found = floor.booths.find((b) => b.linked === boothNumber || b.exhibitor?.['攤位號碼'] === boothNumber);
						if (found) {
							floorIndex = index;
							floorLabel = floor.floor ? `${floor.floor}F` : '';
						}
					}
				});
			}

			if (isPassage && stop['樓層']) {
				floorInfo.forEach((floor, index) => {
					if (String(floor.floor) === String(stop['樓層'])) {
						floorIndex = index;
						floorLabel = floor.floor ? `${floor.floor}F` : '';
					}
				});
			}

			return {
				...stop,
				isBooth,
				isPassage,
				floorIndex,
				floorLabel,
				name,
				intro,
				boothLabel: isBooth ? String.fromCharCode(64 + boothIndex) : null,
				boothIndex: isBooth ? boothIndex : null,
			};
		});
	}, [routeData, exhibitorInfo, floorInfo]);

	const cancelRoute = () => {
		appState.value = {
			...appState.value,
			activeRoute: null,
		};
	};

	const handleStopClick = (stop, index) => {
		if (stop.isBooth) {
			if (stop.floorIndex !== activeFloor && stop.floorIndex >= 0) {
				// Different floor: switch floor first, then select booth
				appState.value = {
					...appState.value,
					activeFloor: stop.floorIndex,
					selectedBooth: stop['攤位號碼'],
					selectedExhibitor: stop['攤位號碼'],
					ExhibitorInfo: true,
				};
			} else {
				// Same floor: open the booth popup
				appState.value = {
					...appState.value,
					selectedBooth: stop['攤位號碼'],
					selectedExhibitor: stop['攤位號碼'],
					ExhibitorInfo: true,
				};
			}
		} else if (stop.isPassage) {
			if (stop.floorIndex >= 0) {
				appState.value = { ...appState.value, activeFloor: stop.floorIndex };
			}
		}
	};

	if (!routeData) return null;

	return (
		<div id='RouteInfoPanel'>
			<div className='route-header'>
				<div className='back-button' onClick={cancelRoute}>
					{translation('route.backToMap')}
				</div>
				<div className='route-name'>{translation('route.routeName')}：{routeData.name}</div>
			</div>
			<div className='route-stops'>
				{resolvedStops.map((stop, index) => {
					const isCurrentFloor = stop.floorIndex === activeFloor;

					if (stop.isBooth) {
						return (
							<div className={`stop clickable ${isCurrentFloor ? '' : 'other-floor'}`} key={`stop-${index}`} onClick={() => handleStopClick(stop, index)}>
								<div className='stop-top'>
									<div className='stop-marker booth-marker'>{stop.boothLabel}</div>
									<div className='stop-title'>
										<div className='stop-number'>第{stop.boothIndex}站</div>
										<div className='stop-name'>{stop.name}</div>
									</div>
									<div className='floor-badge'>{stop.floorLabel}</div>
								</div>
								{stop.intro && <div className='stop-intro'>{stop.intro}</div>}
							</div>
						);
					}

					if (stop.isPassage) {
						return (
							<div className={`stop clickable ${isCurrentFloor ? '' : 'other-floor'}`} key={`stop-${index}`} onClick={() => handleStopClick(stop, index)}>
								<div className='stop-top'>
									<div className='stop-marker passage-marker'>
										<span className='material-symbols-rounded'>stairs</span>
									</div>
									<div className='stop-title'>
										<div className='stop-number'>{translation('route.floorPassage')}</div>
										<div className='stop-name'>{stop.name}</div>
									</div>
									<div className='floor-badge'>{stop.floorLabel}</div>
								</div>
							</div>
						);
					}

					return null;
				})}
			</div>
		</div>
	);
};

export default RouteInfoPanel;
